import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaChevronDown, 
  FaEllipsisH, 
  FaPlay
} from 'react-icons/fa';
import { fetchProjectDetails, fetchRuns } from '../services/api';

import { IoMdClose } from 'react-icons/io';
import MetricsDropdown from '../components/MetricsDropdown';
import MetricDetails from '../components/MetricDetails';

const RunHistory = () => {
  const { projectId } = useParams();
  const [isAuditLoggingEnabled, setIsAuditLoggingEnabled] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [project, setProject] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshData = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      // Fetch project details and runs data
      const [projectData, runsData] = await Promise.all([
        fetchProjectDetails(projectId),
        fetchRuns()
      ]);
      
      // Validate and set project data
      if (projectData && typeof projectData === 'object') {
        setProject(projectData);
      } else {
        throw new Error('Invalid project data received');
      }
      
      // Validate and filter runs data
      if (Array.isArray(runsData)) {
        const projectRuns = runsData.filter(run => 
          run && run.project_id && parseInt(run.project_id) === parseInt(projectId)
        );
        setRuns(projectRuns);
      } else {
        setRuns([]);
      }
    } catch (error) {
      console.error('Error fetching run history:', error);
      // Set error state for better user experience
      setError(error.message || 'Failed to fetch run history');
      setProject(null);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const getRunsForCurrentProject = () => {
    return runs;
  };

  // Format run data to ensure consistent structure for display
  const formatRunData = (run) => {
    if (!run) return null;
    
    return {
      id: run.id || run.run_id || `run-${Date.now()}`,
      timestamp: run.timestamp || run.start_time || run.created_at || 'Unknown',
      status: run.status || run.run_status || 'unknown',
      duration: run.duration || run.execution_duration || 'Unknown',
      tokensUsed: run.tokensUsed || run.token_count || run.tokens_used || 0,
      model: run.model || run.model_name || 'Unknown',
      inputSize: run.inputSize || run.input_size || 'Unknown',
      outputSize: run.outputSize || run.output_size || 'Unknown',
      user: run.user || run.executed_by || run.user_email || 'Unknown',
      // Keep original data for metrics
      ...run
    };
  };

  const handleRunSelection = (run) => {
    const formattedRun = formatRunData(run);
    setSelectedRun(formattedRun);
    setSelectedMetric(null); // Clear selected metric when switching runs
  };

  const handleMetricSelect = (metric) => {
    setSelectedMetric(metric);
  };

  const handleMetricClose = () => {
    setSelectedMetric(null);
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading run history...</div>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-500 text-lg font-medium">Error Loading Run History</div>
        <div className="text-gray-600 text-center max-w-md">{error}</div>
        <button 
          onClick={refreshData} 
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-500 text-lg font-medium">Project Not Found</div>
        <div className="text-gray-600 text-center max-w-md">
          The project with ID {projectId} could not be found or loaded.
        </div>
        <button 
          onClick={refreshData} 
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Project Header */}
      <div className="bg-white -m-6 p-6 mb-6 shadow-sm">
        <div className="flex items-center">
          <Link to={`/projects/${projectId}`} className="text-gray-600 flex items-center">
            <FaArrowLeft className="mr-2" /> Back to Project
          </Link>
        </div>
        
        <div className="mt-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">{project.name} - Run History</h1>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center">
            <FaPlay className="mr-2" /> Test Workflow
          </button>
        </div>
      </div>

      {/* Run History Content */}
      <div className="flex gap-6">
        {/* Left side - Run list */}
        <div className="w-2/3">
          <div className="bg-white rounded-md shadow-sm overflow-hidden">
            <div className="border-b border-gray-200 p-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold">Recent Runs</h2>
              <div className="flex gap-2">
                <button 
                  onClick={refreshData}
                  disabled={loading}
                  className={`flex items-center border border-gray-300 text-gray-600 rounded px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Refresh data"
                >
                  <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
                <button className="flex items-center border border-gray-300 text-gray-600 rounded px-3 py-1 text-sm">
                  <FaCalendarAlt className="mr-2" /> Filter by Date
                </button>
                <button className="flex items-center border border-gray-300 text-gray-600 rounded px-3 py-1 text-sm">
                  <FaChevronDown className="mr-2" /> Status
                </button>
              </div>
            </div>
            
            <div className="divide-y divide-gray-100">
              {getRunsForCurrentProject().length > 0 ? (
                getRunsForCurrentProject().map(run => {
                  const formattedRun = formatRunData(run);
                  return (
                    <div 
                      key={formattedRun.id}
                      onClick={() => handleRunSelection(run)}
                      className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedRun?.id === formattedRun.id ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">{formattedRun.timestamp}</div>
                          <div className="text-sm text-gray-500">
                            Duration: {formattedRun.duration} | Tokens: {formattedRun.tokensUsed}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusClass(formattedRun.status)}`}>
                            {formattedRun.status}
                          </span>
                          <FaEllipsisH className="text-gray-400" />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-6 text-center text-gray-500">
                  No run history available for this project.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Run details */}
        <div className="w-1/3">
          {selectedRun ? (
            <div className="bg-white rounded-md shadow-sm p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Run Details</h3>
                <button onClick={() => setSelectedRun(null)} className="text-gray-500">
                  <IoMdClose />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Timestamp</div>
                  <div>{selectedRun.timestamp}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div className={`inline-block px-2 py-1 rounded-full text-xs ${getStatusClass(selectedRun.status)}`}>
                    {selectedRun.status}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Duration</div>
                  <div>{selectedRun.duration}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Tokens Used</div>
                  <div>{selectedRun.tokensUsed}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Model</div>
                  <div>{selectedRun.model}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Input Size</div>
                  <div>{selectedRun.inputSize}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Output Size</div>
                  <div>{selectedRun.outputSize}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Executed By</div>
                  <div>{selectedRun.user}</div>
                </div>
                
                {/* Metrics Dropdown */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="mb-4">
                    <div className="text-sm text-gray-500 mb-2">Metrics Analysis</div>
                    <MetricsDropdown 
                      run={selectedRun} 
                      onMetricsSelect={handleMetricSelect}
                    />
                  </div>
                </div>
                
                {/* Selected Metric Details */}
                {selectedMetric && (
                  <MetricDetails 
                    selectedMetric={selectedMetric} 
                    onClose={handleMetricClose}
                  />
                )}
                
                <div className="pt-4 border-t border-gray-100">
                  <button className="w-full bg-blue-600 text-white py-2 rounded-md">
                    View Complete Logs
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-md shadow-sm p-6 text-center text-gray-500">
              Select a run to view details
            </div>
          )}
          
          <div className="bg-white rounded-md shadow-sm p-4 mt-6">
            <h3 className="font-semibold mb-4">Run Settings</h3>
            
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm">Audit Logging</span>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={isAuditLoggingEnabled}
                  onChange={() => setIsAuditLoggingEnabled(!isAuditLoggingEnabled)}
                  className="sr-only"
                />
                <div className={`w-10 h-5 rounded-full ${isAuditLoggingEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                  <div className={`h-4 w-4 rounded-full bg-white transform transition-transform ${isAuditLoggingEnabled ? 'translate-x-5' : 'translate-x-1'} mt-0.5`}></div>
                </div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm">Auto-archive Runs (7 days)</span>
              <label className="switch">
                <input type="checkbox" className="sr-only" />
                <div className="w-10 h-5 rounded-full bg-gray-300">
                  <div className="h-4 w-4 rounded-full bg-white transform translate-x-1 mt-0.5"></div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunHistory; 